import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, TemplateRef, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { detailExpandAnimation } from '@app/api/core/utils/helper/offerteantraege-helper';
import { NoDataComponent } from '@app/shared/components/no-data/no-data.component';
import { ThrottleEventDirective } from '@app/shared/directives/throttle-event.directive';
import { FormatAdressPipe } from '@app/shared/pipes/formatadress.pipe';
import { FormatDatePipe } from '@app/shared/pipes/formatdate.pipe';
import { FormatNumberPipe } from '@app/shared/pipes/formatnumber.pipe';
import { Action } from '@app/shared/services/ui/dialog/dialog.types';
import { MessageType } from '@app/shared/services/ui/message/message';
import { BasicPolizzeListComponent } from '@app/views/vertriebscockpit/offerteantraege/basic-polizze-list/basic-polizze-list.component';
import { AntragDocumentUploadDialogComponent } from '@app/views/vertriebscockpit/offerteantraege/makler/nicht-freigegebene-antraege/antrag-document-upload-dialog/antrag-document-upload-dialog.component';
import { VollmachtDialogComponent } from '@app/views/vertriebscockpit/offerteantraege/makler/nicht-freigegebene-antraege/vollmacht-dialog/vollmacht-dialog.component';
import { getDocumentStatusText, getSimpleVollmachtStatus, getVollmachtStatusText, maklerDocumentExists, MaklerDocumentLayoutConfig, SIMPLE_VOLLMACHT_STATUS_CONFIG } from '@app/views/vertriebscockpit/offerteantraege/makler/offerteantraege-makler.helper';
import { MaklerDocumentType, MaklerPolizzeDetails, PolizzeResult } from '@o30/entities';
import { filter, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector:        'app-nicht-freigegebene-antraege',
  templateUrl:     './nicht-freigegebene-antraege.component.html',
  styleUrls:       [
    './nicht-freigegebene-antraege.component.scss',
    '../../offertstagetwo/offertstagetwo.component.scss',
    '../../offertstagetwo/_offertstagetwo-theme.scss'
  ],
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports:         [
    AsyncPipe,
    FormatAdressPipe,
    FormatDatePipe,
    MatButton,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatIconButton,
    MatPaginator,
    MatProgressSpinner,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatTable,
    MatTooltip,
    NgTemplateOutlet,
    NoDataComponent,
    NgClass,
    MatHeaderCellDef,
    ThrottleEventDirective
  ],
  animations:      [detailExpandAnimation]
})
export class NichtFreigegebeneAntraegeComponent extends BasicPolizzeListComponent {

  @ViewChild('check_true', {static: true}) checkTrue!: TemplateRef<void>;
  @ViewChild('check_false', {static: true}) checkFalse!: TemplateRef<void>;
  @ViewChild('check_false_green', {static: true}) checkFalseGreen!: TemplateRef<void>;

  // TODO description
  protected documentLayoutConfig: MaklerDocumentLayoutConfig[] = [
    {
      label:                        'Vollmachtsdokument',
      actionLabel:                  'Vollmacht',
      description:                  'Im Punkt „Vollmachtsdokument“ ersuchen wir Sie um Mitteilung, ob bei der NV bereits ein entsprechendes Dokument aufliegt. Sollte dies nicht der Fall sein, bitten wir um Information, ob ein Vollmachtsdokument hochgeladen wird oder alternativ ein unterschriebenes Antragsdokument zur Verfügung gestellt wird.',
      documentType:                 MaklerDocumentType.VOLLMACHT,
      getStatusTextFunction:        getVollmachtStatusText,
      getStatusTemplateRefFunction: this.getVollmachtStatusTemplateRef.bind(this)
    },
    {
      label:                        'Genehmigungsdokument',
      actionLabel:                  'Upload',
      description:                  'Im Punkt „Genehmigungsdokument“ können sämtliche Unterlagen hochgeladen werden, die für eine Genehmigung erforderlich sind – etwa bei der Beantragung eines höheren Rabatts, einer individuellen Klausel oder ähnlicher Sonderregelungen.',
      documentType:                 MaklerDocumentType.GENEHMIGUNG,
      getStatusTextFunction:        getDocumentStatusText,
      getStatusTemplateRefFunction: this.getDocumentStatusIconTemplateRef.bind(this)
    }
  ];

  get displayedColumns() {
    const columns = this.tableColumns.map(c => c.columnDef);
    columns.unshift('expand');
    if (this.bearbeitenErlaubt) {
      columns.push('aktionen');
    }
    return columns;
  }

  getAnzRowsPerEntry(): number {
    return 2;
  }

  protected openUploadFileDialog(polizze: PolizzeResult, documentType: MaklerDocumentType) {
    if (documentType === MaklerDocumentType.GENEHMIGUNG) {
      const config = {
        width: '800px'
      };
      this.reactiveDialogService.open(AntragDocumentUploadDialogComponent, config,
          polizze, {
            maklerDocumentType: documentType as MaklerDocumentType,
            simpleDocument:     undefined // in case we need to display the existing document in the dialog
          }
        )
        .afterClosed()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(_dialogResult => _dialogResult?.status === 'success')
        )
        .subscribe(_dialogResult =>
          polizze.maklerPolizzeDetails = _dialogResult.data);
    } else if (documentType === MaklerDocumentType.VOLLMACHT) {
      const config = {
        width: '800px'
      };
      this.reactiveDialogService.open(VollmachtDialogComponent, config, polizze, {
            maklerDocumentType: documentType as MaklerDocumentType,
            simpleDocument:     null
          }
        )
        .afterClosed()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(_dialogResult => _dialogResult?.status === 'success'))
        .subscribe(_dialogResult => {
          polizze.maklerPolizzeDetails = _dialogResult.data;
        });
    }
  }

  protected antragSenden(polizze: PolizzeResult) {
    this.messageService.showSimpleOkCancelDialog('Soll der Antrag gesendet werden?')
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        filter(_dialogResult => _dialogResult?.status === 'ok'),
        tap(_ => this.llapi.patchValue('layout.spinner', true)),
        switchMap(_dialogResult => this.dataService.sendPoliceToMaklerBackoffice(polizze.id)),
        filter(_payload => this.messageService.handlePayloadReturnSuccess(_payload))
      )
      .subscribe(_payload => {
          this.llapi.patchValue('layout.spinner', false);
          this.messageService.showDialog({
            text: `Der Antrag zur Polizzennummer ${new FormatNumberPipe().transform(polizze.polizzennummer, 'polizze')} wurde erfolgreich an die NV AG übermittelt.`,
            type: MessageType.SUCCESS
          }, {
            data: {
              title:        'Antrag erfolgreich gesendet',
              buttonConfig: {
                visible:       true,
                alignment:     'right',
                actionButtons: [
                  {
                    name:  'OK',
                    color: 'accent',
                    value: Action.OK
                  }
                ]
              }
            }
          });
          this.removeFromList(polizze.id);
        }
      );
  }

  protected isAntragSendenDisabled(polizze: PolizzeResult): boolean {
    const simpleVollmachtStatus = getSimpleVollmachtStatus(polizze.maklerPolizzeDetails);
    // TODO additional disabled conditions if requred
    if (SIMPLE_VOLLMACHT_STATUS_CONFIG[simpleVollmachtStatus]?.checkOk) {
      return false;
    }
    return true;
  }

  // ============= VOLLMACHT STATUS =============

  protected getVollmachtStatusTemplateRef(details: MaklerPolizzeDetails) {
    const simpleVollmachtStatus = getSimpleVollmachtStatus(details);
    const checkOk = SIMPLE_VOLLMACHT_STATUS_CONFIG[simpleVollmachtStatus]?.checkOk;
    return this.getCheckTemplateRef(checkOk);
  }

  // ============= GENEHMIGUNG STATUS =============

  protected getDocumentStatusIconTemplateRef(details: MaklerPolizzeDetails): TemplateRef<void> {
    const documentType = MaklerDocumentType.GENEHMIGUNG; // vollmacht is now handled differently
    const documentExists = maklerDocumentExists(details, documentType);
    if (!documentExists) {
      return this.checkFalseGreen;
    }
    return this.getCheckTemplateRef(documentExists);
  }

  // ================================

  private getCheckTemplateRef(checkOk: boolean) {
    if (checkOk) {
      return this.checkTrue;
    } else {
      return this.checkFalse;
    }
  }
}
